import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { IUserLogado } from "../../Services/Interfaces";
import { userServiceRescue } from "../../Services/User";
import {
  ICustomerService,
  IUserServiceRescueIOkRest,
  VitalSignsMeasurementsArray,
} from "../../Services/Interfaces";
import {
  RoundUp,
  intervalsRange,
  verificaTipoExternalId,
} from "../../utils/conversions";

function PageMeasurementThermalGraph() {
  const { criteriaSrId } = useParams();
  const [userLogado, setUserLogado] = useState<IUserLogado>();
  const [vitalSignsMeasurements, setVitalSignsMeasurements] =
    useState<VitalSignsMeasurementsArray>();
  const [createdAt, setCreatedAt] = useState("");
  const [customerService, setCustomerService] = useState<ICustomerService>();

  useEffect(() => {
    const userJSON = localStorage.getItem("user");
    if (userJSON) {
      const userObjeto = JSON.parse(userJSON) as IUserLogado;
      setUserLogado(userObjeto);
    }
  }, []);

  useEffect(() => {
    if (criteriaSrId) {
      userServiceRescue(undefined, undefined, criteriaSrId).then((response) => {
        const { data } = response as IUserServiceRescueIOkRest;
        if (data.data.length > 0) {
          if (data.data[0].vitalSignsMeasurementsArray) {
            setVitalSignsMeasurements(data.data[0].vitalSignsMeasurementsArray);
            setCreatedAt(data.data[0].createdAt);
            setCustomerService(data.data[0].customerService);
          }
        }
      });
    }
  }, [criteriaSrId]);

  const createAsciiGraph = (
    value: number,
    ranges: number[],
    colors: string[],
    unit: string,
    title: string
  ) => {
    const lineWidth = 65; // Largura total da linha
    const graphWidth = lineWidth; // O gráfico ocupa a linha toda
    
    // Determinar posição do valor no gráfico
    let position = 0;
    let valueZone = 0;
    
    if (colors.length === 3) {
      // Gráfico de 3 cores
      if (value < ranges[0]) {
        position = Math.floor(graphWidth * 0.25);
        valueZone = 0; // Amarelo (primeira faixa)
      } else if (value >= ranges[1] && value < ranges[2]) {
        position = Math.floor(graphWidth * 0.5);
        valueZone = 1; // Verde (faixa do meio)
      } else if (value > ranges[3]) {
        position = Math.floor(graphWidth * 0.75);
        valueZone = 2; // Amarelo (última faixa)
      }
    } else if (colors.length === 5) {
      // Gráfico de 5 cores
      if (value < ranges[0]) {
        position = Math.floor(graphWidth * 0.1);
        valueZone = 0; // Vermelho escuro
      } else if (value >= ranges[1] && value < ranges[2]) {
        position = Math.floor(graphWidth * 0.3);
        valueZone = 1; // Vermelho claro
      } else if (value >= ranges[3] && value < ranges[4]) {
        position = Math.floor(graphWidth * 0.5);
        valueZone = 2; // Verde claro
      } else if (value >= ranges[5] && value < ranges[6]) {
        position = Math.floor(graphWidth * 0.7);
        valueZone = 3; // Verde forte
      } else if (value >= ranges[7]) {
        position = Math.floor(graphWidth * 0.9);
        valueZone = 4; // Amarelo
      }
    }

    // Criar barra sólida toda na cor do "BOM" (cinza claro)
    let graphBar = "";
    
    for (let i = 0; i < graphWidth; i++) {
      const isValuePosition = i === position;
      
      if (isValuePosition) {
        graphBar += "⬤"; // Valor atual - indicador maior
      } else {
        graphBar += "░"; // Toda a barra na cor do BOM (cinza claro)
      }
    }

    // Criar linha de status centralizada apenas se a métrica tiver status
    let statusLine = "";
    if (colors.length > 0) {
      if (colors.length === 5) {
        const statusWidth = Math.floor(graphWidth / 5);
        // Mapear status baseado nas cores reais
        for (let i = 0; i < colors.length; i++) {
          const color = colors[i];
          let status = "";
          
          // Mapear cores para status baseado nas cores reais do HTML
          if (color === "#FF5A56" || color === "#CC444B") {
            status = "Alterado"; // Vermelho
          } else if (color === "#ff8280") {
            status = "Atenção"; // Vermelho claro
          } else if (color === "#FFBB00") {
            status = "Alerta"; // Amarelo
          } else if (color === "#52B788" || color === "#74C69D") {
            status = "Bom"; // Verde/Verde claro
          } else if (color === "#40916C") {
            status = "Muito Bom"; // Verde escuro
          } else {
            // Fallback baseado na posição
            if (i === 0) status = "Alterado";
            else if (i === 1) status = "Atenção";
            else if (i === 2) status = "Alerta";
            else if (i === 3) status = "Bom";
            else status = "Muito Bom";
          }
          
          // Centralizar o status dentro da sua faixa
          const padding = Math.max(0, Math.floor((statusWidth - status.length) / 2));
          const leftPadding = " ".repeat(padding);
          const rightPadding = " ".repeat(Math.max(0, statusWidth - status.length - padding));
          statusLine += leftPadding + status + rightPadding;
        }
      } else if (colors.length === 3) {
        const statusWidth = Math.floor(graphWidth / 3);
        
        // Centralizar cada status
        const statuses = ["Alerta", "Bom", "Alerta"];
        for (let i = 0; i < statuses.length; i++) {
          const status = statuses[i];
          const padding = Math.max(0, Math.floor((statusWidth - status.length) / 2));
          const leftPadding = " ".repeat(padding);
          const rightPadding = " ".repeat(Math.max(0, statusWidth - status.length - padding));
          statusLine += leftPadding + status + rightPadding;
        }
      }
    }

    // Exibir texto em uma linha, gráfico na linha seguinte, status na próxima
    return colors.length > 0 
      ? `${title} ${unit} ${value}\n${graphBar}\n${statusLine}`
      : `${title} ${unit} ${value}`;
  };

  const createSimpleGraph = (value: number, unit: string, title: string) => {
    const lineWidth = 65;
    const graphWidth = lineWidth;
    
    // Criar barra sólida toda na cor do "BOM" (cinza claro)
    const barLength = Math.min(Math.floor((value / 100) * graphWidth), graphWidth);
    let bar = "";
    for (let i = 0; i < graphWidth; i++) {
      if (i < barLength) {
        bar += "░"; // Toda a barra na cor do BOM (cinza claro)
      } else {
        bar += " "; // Vazio
      }
    }

    return `${title}${unit ? ' ' : ''}${unit} ${value}\n${bar}`;
  };

  const formatThermalGraphPrint = () => {
    if (!vitalSignsMeasurements || !userLogado || !customerService) return "";

    let output = "";
    const lineWidth = 65;
    const centerText = (text: string) => {
      const padding = Math.max(0, Math.floor((lineWidth - text.length) / 2));
      return " ".repeat(padding) + text;
    };

    // Cabeçalho
    output += "=".repeat(lineWidth) + "\n";
    output += centerText("PROGRAMA " + customerService.program.name.toUpperCase()) + "\n";
    output += "=".repeat(lineWidth) + "\n";
    output += centerText("RESULTADO DA MEDIÇÃO COM GRÁFICOS") + "\n";
    output += "-".repeat(lineWidth) + "\n";
    
    // Informações do usuário
    output += `Paciente: ${userLogado.profile.fullName}\n`;
    output += `Data: ${new Date(createdAt).toLocaleDateString()}\n`;
    output += `Hora: ${new Date(createdAt).toLocaleTimeString()}\n`;
    output += "-".repeat(lineWidth) + "\n";

    // SNR se disponível
    if (vitalSignsMeasurements["vazio"]) {
      const snrValue = verificaTipoExternalId("SNR", vitalSignsMeasurements["vazio"], 2);
      output += `SNR: ${snrValue}\n`;
      output += "-".repeat(lineWidth) + "\n";
    }

    // Medições por grupo com gráficos
    Object.keys(vitalSignsMeasurements).forEach((key) => {
      if (key === "vazio") return;
      const measurements = vitalSignsMeasurements[key];
      if (measurements.length > 0) {
        output += centerText(measurements[0].displayGroup) + "\n";
        output += "-".repeat(lineWidth) + "\n";
        measurements.forEach((vitalSign) => {
          const value = RoundUp(vitalSign.value, parseInt(vitalSign.presentation || "0"));
          if (value === 0) {
            return;
          } else if (intervalsRange(vitalSign.intervalsRange).colors.length === 3) {
            const graph = createAsciiGraph(
              value,
              intervalsRange(vitalSign.intervalsRange).ranges,
              intervalsRange(vitalSign.intervalsRange).colors,
              vitalSign.unit || "",
              vitalSign.name
            );
            output += graph + "\n\n";
          } else if (intervalsRange(vitalSign.intervalsRange).colors.length === 5) {
            const graph = createAsciiGraph(
              value,
              intervalsRange(vitalSign.intervalsRange).ranges,
              intervalsRange(vitalSign.intervalsRange).colors,
              vitalSign.unit || "",
              vitalSign.name
            );
            output += graph + "\n\n";
          } else if (vitalSign.graphicFormat === "INTEIRO" && intervalsRange(vitalSign.intervalsRange).colors.length > 0) {
            const graph = createSimpleGraph(
              value,
              vitalSign.unit || "",
              vitalSign.name
            );
            output += graph + "\n\n";
          } else {
            // Circunferência da Cintura centralizada
            if (vitalSign.name.toLowerCase().includes("circunferência")) {
              output += centerText(`${vitalSign.name}: ${value} ${vitalSign.unit || ""}`) + "\n\n";
            } else {
              output += `${vitalSign.name}: ${value} ${vitalSign.unit || ""}\n\n`;
            }
          }
        });
      }
    });

    // Rodapé centralizado
    output += centerText("ATENÇÃO!") + "\n";
    output += centerText("Estes índices não deverão substituir a") + "\n";
    output += centerText("avaliação de um profissional de Saúde.") + "\n";
    output += centerText("São apenas indicadores de um programa") + "\n";
    output += centerText("de bem-estar.") + "\n";
    output += "=".repeat(lineWidth) + "\n";
    output += "\n\n\n"; // Espaço para cortar papel

    return output;
  };

  const handlePrint = () => {
    if (vitalSignsMeasurements && userLogado && customerService) {
      const thermalContent = formatThermalGraphPrint();
      
      // Abrir nova janela com o conteúdo formatado
      const newWindow = window.open("", "_blank");
      if (newWindow) {
        newWindow.document.write(`
          <html>
            <head>
              <title>Impressão Térmica com Gráficos</title>
              <style>
                body { 
                  font-family: monospace; 
                  font-size: 14px; 
                  line-height: 1.1;
                  margin: 0;
                  padding: 10px;
                  white-space: pre;
                  font-weight: bold;
                }
              </style>
              <script>
                // Fechar a janela após imprimir ou cancelar
                window.onafterprint = function() {
                  window.close();
                };
                
                // Fechar a janela se o usuário cancelar (detectar mudança de foco)
                window.onbeforeunload = function() {
                  return null;
                };
                
                // Tentar detectar cancelamento de impressão
                let printTimeout;
                window.onfocus = function() {
                  clearTimeout(printTimeout);
                  printTimeout = setTimeout(function() {
                    window.close();
                  }, 1000);
                };
              </script>
            </head>
            <body>${thermalContent}</body>
          </html>
        `);
        newWindow.document.close();
        
        // Aguardar um pouco para o conteúdo carregar antes de imprimir
        setTimeout(() => {
          newWindow.print();
        }, 100);
      }
    }
  };

  return (
    <div style={{ 
      fontFamily: 'monospace', 
      fontSize: '24px', 
      lineHeight: '1.2',
      whiteSpace: 'pre-wrap',
      padding: '20px',
      backgroundColor: '#f5f5f5'
    }}>
      {vitalSignsMeasurements && userLogado && customerService ? (
        <div style={{ textAlign: 'center' }}>
          <h3>Visualização para Impressora Térmica com Gráficos (80 colunas)</h3>
          <button 
            onClick={handlePrint}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              marginBottom: '20px'
            }}
          >
            Imprimir
          </button>
          <div 
            style={{ 
              backgroundColor: 'white', 
              padding: '20px', 
              border: '1px solid #ccc',
              fontFamily: 'monospace',
              fontSize: '20px',
              whiteSpace: 'pre-wrap',
              lineHeight: '1.2',
              overflow: 'auto',
              textAlign: 'left',
              fontWeight: 'bold'
            }}
            dangerouslySetInnerHTML={{ __html: formatThermalGraphPrint().replace(/\n/g, '<br/>') }}
          />
        </div>
      ) : (
        <div>Carregando dados para impressão térmica com gráficos...</div>
      )}
    </div>
  );
}

export default PageMeasurementThermalGraph; 